import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';

import { AtlasIcon } from '@crossnokaye/ux-animatics';
import { Skeleton } from '@crossnokaye/ux-common';

import type { CompressorScheduleForecast } from '@repo/sdk/models/components';

import { SequencerLegend } from '@/features/equipment-browser/sequencer-section/components/sequencer-legend';
import { SequencerSectionTemplate } from '@/features/equipment-browser/sequencer-section/components/sequencer-section-template';
import {
  addUTCMilliseconds,
  createUTCDate,
  subtractUTCMilliseconds,
} from '@/helpers/datetime/utc-date';
import { useGetCompressorScheduleForecast } from '@/hooks/query-hooks/gaia-api/scheduled-apps';
import { useCurrentFacility } from '@/hooks/use-current-facility';

import {
  CHART_GRID_BOTTOM,
  CHART_GRID_TOP,
  CHART_ROW_HEIGHT,
  FORECAST_MINUTES,
  LOOKBACK_MINUTES,
} from './compressor-schedule-chart.constants';
import { useCompressorScheduleChartOptions } from './use-compressor-schedule-chart-options';
import { trimBackground } from '../helpers';

const PRIMARY_VAR = 'hsl(var(--primary))';
const FORECAST_WINDOW_REFRESH_MS = 30_000;

const scheduleChartLegendItems = [
  {
    icon: <div className="w-4 h-4 bg-primary rounded-full" />,
    label: 'On',
  },
  {
    icon: <div className="w-4 h-4 rounded-full" style={{ background: trimBackground }} />,
    label: 'Trim',
  },
  {
    icon: (
      <div
        className="w-4 h-4 rounded-full border border-white/50"
        style={{ background: `linear-gradient(to right, transparent, ${PRIMARY_VAR})` }}
      />
    ),
    label: 'Ramping Up',
  },
  {
    icon: (
      <div
        className="w-4 h-4 rounded-full border border-white/50"
        style={{ background: `linear-gradient(to left, transparent, ${PRIMARY_VAR})` }}
      />
    ),
    label: 'Ramping Down',
  },
];

interface CompressorScheduleChartContentProps {
  compressorOrder: string[];
  forecast: CompressorScheduleForecast;
  now: number;
  showNotInControl?: boolean;
}

const ScheduleChartRowLabels = ({ compressorOrder }: { compressorOrder: string[] }) => {
  return (
    <div
      className="w-[140px] shrink-0"
      style={{ paddingTop: CHART_GRID_TOP, paddingBottom: CHART_GRID_BOTTOM }}
    >
      {compressorOrder.map((compressor) => (
        <div
          key={compressor}
          className="px-2 flex items-center h-[inherit] border-l-0 border-b-1 bg-muted"
          style={{ height: CHART_ROW_HEIGHT }}
        >
          <div className="flex items-center justify-left gap-2 text-secondary-foreground text-sm font-semibold w-full">
            <AtlasIcon symbol="compressor" size="1rem" className="fill-muted-foreground" />
            <span className="truncate">{compressor}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const CompressorScheduleChartContent = ({
  compressorOrder,
  forecast,
  now,
  showNotInControl = false,
}: CompressorScheduleChartContentProps) => {
  const options = useCompressorScheduleChartOptions(forecast, now, compressorOrder);
  const chartHeight =
    CHART_GRID_TOP + compressorOrder.length * CHART_ROW_HEIGHT + CHART_GRID_BOTTOM;

  return (
    <div className="flex flex-col">
      {showNotInControl ? (
        <div className="mb-4 rounded-md border border-ckds-base-caution/30 bg-ckds-base-caution/10 px-3 py-2 text-sm font-semibold text-ckds-base-caution">
          Cosmic sequencer is not currently in control. This forecast is informational right now.
        </div>
      ) : null}
      <div className="flex w-full">
        <ScheduleChartRowLabels compressorOrder={compressorOrder} />
        <ReactECharts
          option={options}
          notMerge
          style={{ height: chartHeight }}
          className="w-full"
          opts={{ renderer: 'canvas' }}
          lazyUpdate
        />
      </div>
    </div>
  );
};

interface CompressorScheduleChartProps {
  compressorOrder: string[];
  showNotInControl?: boolean;
}

export const CompressorScheduleChart = ({
  compressorOrder,
  showNotInControl = false,
}: CompressorScheduleChartProps) => {
  const { agentId } = useCurrentFacility();
  const [windowAnchor, setWindowAnchor] = useState(() => createUTCDate());

  useEffect(() => {
    const syncWindowAnchor = () => {
      setWindowAnchor(createUTCDate());
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncWindowAnchor();
      }
    };

    const interval = window.setInterval(syncWindowAnchor, FORECAST_WINDOW_REFRESH_MS);
    window.addEventListener('focus', syncWindowAnchor);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', syncWindowAnchor);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const { since, until } = useMemo(() => {
    return {
      since: subtractUTCMilliseconds(LOOKBACK_MINUTES * 60 * 1000, windowAnchor),
      until: addUTCMilliseconds(FORECAST_MINUTES * 60 * 1000, windowAnchor),
    };
  }, [windowAnchor]);

  const {
    data: forecast,
    isLoading,
    dataUpdatedAt,
  } = useGetCompressorScheduleForecast({
    agentId,
    since,
    until,
  });

  if (isLoading && !forecast) {
    return <Skeleton className="flex-1 h-32 bg-ckds-neutral-700" />;
  }

  if (!forecast || forecast.steps.length === 0) {
    return null;
  }

  return (
    <SequencerSectionTemplate
      title={'COSMIC FORECAST'}
      legend={<SequencerLegend items={scheduleChartLegendItems} />}
    >
      <CompressorScheduleChartContent
        compressorOrder={compressorOrder}
        forecast={forecast}
        now={dataUpdatedAt || windowAnchor.getTime()}
        showNotInControl={showNotInControl}
      />
    </SequencerSectionTemplate>
  );
};
